import LocalStorageService from '@/services/localStorageService';
import platformAPI from '@/services/platformAPI';
import dchAPI from '@/services/dchAPI';
import config from '@/config';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isSuperAdminToken } from '@/services/superAdmin';
dayjs.extend(utc);

// Helper function to extract token expiry (ISO string) from JWT
function getTokenExpiryFromJwt(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    return exp ? new Date(exp * 1000).toISOString() : null;
  } catch (error) {
    return null;
  }
}

// Helper function to decode JWT and extract companies from groups claim
const extractCompaniesFromToken = (token) => {
  if (!token) return null;

  try {
    // Decode JWT payload (middle part of the token)
    const payload = JSON.parse(atob(token.split('.')[1]));

    if (!payload.groups || !Array.isArray(payload.groups)) {
      return null;
    }

    // Extract companies from groups that match the pattern "company:uuid_name"
    const companies = payload.groups
      .filter((group) => group.startsWith('company:'))
      .map((group) => {
        // Remove "company:" prefix
        const withoutPrefix = group.substring(8);
        // Split by underscore to get id and name
        const firstUnderscoreIndex = withoutPrefix.indexOf('_');
        if (firstUnderscoreIndex === -1) return null;

        const id = withoutPrefix.substring(0, firstUnderscoreIndex);
        const name = withoutPrefix.substring(firstUnderscoreIndex + 1).replace(/_/g, ' ');

        return { id, name };
      })
      .filter((company) => company !== null)
      .sort((a, b) => a.name.localeCompare(b.name));

    return companies.length > 0 ? companies : null;
  } catch (error) {
    console.error('Error extracting companies from token:', error);
    return null;
  }
};

const state = {
  token: LocalStorageService.getAccessToken() || null,
  tokenExpiry: LocalStorageService.getTokenExpiry() || null,
  refreshToken: LocalStorageService.getRefreshToken() || null,
  userEmail: LocalStorageService.getUserEmail() || '',
  authIdToken: null,
  authAccessToken: null,
  authRefreshToken: null,
  authChallenge: null,
  authResetResponse: null,
  tokenCompanies: extractCompaniesFromToken(LocalStorageService.getAccessToken()),
};

const getters = {
  isLoggedIn(state) {
    const { token } = state;
    return !!token;
  },
  getUserEmail(state) {
    return state.userEmail;
  },
  isAuthenticated: (state) => !!state.authIdToken,
  authChallenge: (state) => state.authChallenge,
  authResetResponse: (state) => state.authResetResponse,
  tokenCompanies: (state) => state.tokenCompanies,
  isAdministrator: (state) => state.tokenCompanies && state.tokenCompanies.length > 0,
  isSuperAdmin: (state) => isSuperAdminToken(state.token),
  userIdentity: (state) => state.userEmail,
};

const actions = {
  logout(context) {
    if (context.getters.isLoggedIn) {
      context.commit('logout');
      context.commit('clearAuth');
    }
  },
  refreshTokenLogin(context, { token, refreshToken }) {
    context.commit('refreshTokenLogin', {
      token,
      refreshToken,
    });
  },
  login(context, credentials) {
    return new Promise((resolve, reject) => {
      const email = credentials.email;
      platformAPI
        .post('login', {
          email: credentials.email,
          password: credentials.password,
        })
        .then((response) => {
          if (!response.data.dch) {
            let error = new Error('Ankeri DCH rights are required');
            error.status = 403;
            error.response = { data: { message: 'Ankeri DCH rights are required' } };
            throw error;
          }

          const { token, refreshToken, exp: tokenExpiry, dchCompanyId: companyId } = response.data;

          context.commit('setTokenLogin', {
            token,
            tokenExpiry,
            refreshToken,
            email,
            companyId,
          });
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  startlogin(context, credentials) {
    return new Promise((resolve, reject) => {
      dchAPI
        .post('/auth/login', {
          username: credentials.username,
          password: credentials.password,
        })
        .then((response) => {
          const login_response = response.data.data;
          // important here to check if the response is a challenge first
          if (login_response.ChallengeName) {
            context.commit('setAdminChallenge', login_response);
          } else if (login_response.AuthenticationResult) {
            context.commit('setAdminTokens', login_response.AuthenticationResult);
          }
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  respondToAuthChallenge(context, challengeResponse) {
    return new Promise((resolve, reject) => {
      dchAPI
        .post('/auth/challenge', challengeResponse)
        .then((response) => {
          const login_response = response.data.data;
          // important here to check if the response is a challenge first
          if (login_response.ChallengeName) {
            context.commit('setAdminChallenge', login_response);
          } else if (login_response.AuthenticationResult) {
            context.commit('setAdminTokens', login_response.AuthenticationResult);
          } else if (login_response.token) {
            const {
              email,
              token,
              refreshToken,
              exp: tokenExpiry,
              dchCompanyId: companyId,
            } = login_response;
            context.commit('setAdminLogin', {
              token,
              tokenExpiry,
              refreshToken,
              email,
              companyId,
            });
          }
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  refreshAuth(context, { token, tokenExpiry }) {
    context.commit('setAdminTokenRefresh', {
      token,
      tokenExpiry,
    });
  },
  resetPassword(context, input) {
    return new Promise((resolve, reject) => {
      dchAPI
        .post('/auth/reset', input)
        .then((response) => {
          context.commit('authResetResponse', response.data);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  confirmResetPassword(context, input) {
    return new Promise((resolve, reject) => {
      dchAPI
        .post('/auth/reset/confirm', input)
        .then((response) => {
          context.commit('authResetResponse', response.data);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async handleAnkeriAuthCallback(context, { code, state }) {
    const storedState = sessionStorage.getItem('auth_state');
    if (!storedState || storedState !== state) {
      throw new Error('Invalid state parameter. Possible CSRF attack.');
    }

    try {
      // platform_url tells DCH which Ankeri Platform issued this code, so it
      // exchanges against the same one the user just logged in to.
      const response = await dchAPI.post('/auth/exchange/dch', {
        code,
        platform_url: config.PLATFORM_URL,
      });
      const data = response.data?.data ?? response.data;
      const token = data?.token;
      const email = data?.email;
      const dchCompanyId = data?.dch_company_id ?? data?.dchCompanyId;
      const refreshToken = data?.refreshToken ?? data?.refresh_token;

      if (!token) {
        throw new Error('No token received from authentication server');
      }

      const tokenExpiry = getTokenExpiryFromJwt(token);
      context.commit('setTokenLogin', {
        token,
        tokenExpiry,
        refreshToken,
        email,
        companyId: dchCompanyId,
      });
      return data;
    } catch (error) {
      console.error('Token exchange error:', error);
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data || {};
        const message = data.message ?? data.errors?.[0]?.detail;
        const accessDeniedFallback = 'You do not have access to DCH Admin. Please contact your administrator.';
        if (status === 403) {
          throw new Error(message || accessDeniedFallback);
        }
        throw new Error(message || 'Token exchange failed');
      }
      throw error;
    }
  },

  switchCompany(context, companyId) {
    return new Promise((resolve, reject) => {
      const authRefreshToken = LocalStorageService.getAuthRefreshToken();
      if (!authRefreshToken) {
        reject(new Error('No refresh token available'));
        return;
      }

      const dchUrl =
        process.env.NODE_ENV === 'development' ? '/dchapi/' : config.DCH_API_URL;

      // Using axios directly to avoid interceptors
      const axios = require('axios');
      axios
        .post(`${dchUrl}auth/refresh`, {
          refreshToken: authRefreshToken,
          ActAsCompanyId: companyId,
        })
        .then((response) => {
          const { token, exp: tokenExpiry } = response.data.data;

          context.commit('setAdminTokenRefresh', {
            token,
            tokenExpiry,
          });

          LocalStorageService.setDchCompanyId(companyId);

          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // Proactively check token expiry and refresh if needed
  async ensureValidToken(context) {
    const tokenExpiry = LocalStorageService.getTokenExpiry();
    const authRefreshToken = LocalStorageService.getAuthRefreshToken();
    const platformRefreshToken = LocalStorageService.getRefreshToken();

    // If no token or no refresh token available, logout
    if (!context.getters.isLoggedIn) {
      return Promise.resolve();
    }

    if (!authRefreshToken && !platformRefreshToken) {
      context.commit('logout');
      context.commit('clearAuth');
      return Promise.reject(new Error('No refresh token available'));
    }

    // Check if token is expired or will expire soon (within 5 minutes)
    const now = dayjs.utc();
    const expiry = dayjs.utc(tokenExpiry);
    const minutesUntilExpiry = expiry.diff(now, 'minute');

    // If token is still valid for more than 5 minutes, no need to refresh
    if (tokenExpiry && minutesUntilExpiry > 5) {
      return Promise.resolve();
    }

    // Token is expired or expiring soon, attempt refresh
    if (authRefreshToken) {
      try {
        const dchUrl =
          process.env.NODE_ENV === 'development'
            ? '/dchapi/'
            : config.DCH_API_URL;
        
        const axios = require('axios');
        const res = await axios.post(`${dchUrl}auth/refresh`, {
          refreshToken: authRefreshToken,
          ActAsCompanyId: LocalStorageService.getDchCompanyId(),
        });

        const { token, exp: tokenExpiry } = res.data.data;

        context.commit('setAdminTokenRefresh', {
          token,
          tokenExpiry,
        });

        return Promise.resolve();
      } catch (error) {
        // Refresh failed, logout user
        context.commit('logout');
        context.commit('clearAuth');
        return Promise.reject(error);
      }
    } else if (platformRefreshToken) {
      try {
        const axios = require('axios');
        if (config.ANKERI_AUTH_ENABLED) {
          const dchUrl =
            process.env.NODE_ENV === 'development'
              ? '/dchapi/'
              : config.DCH_API_URL;
          const res = await axios.post(`${dchUrl}auth/refresh/dch`, {
            refreshToken: platformRefreshToken,
            platform_url: config.PLATFORM_URL,
          });

          const { token, refreshToken } = res.data;
          const tokenExpiry = getTokenExpiryFromJwt(token);

          context.commit('refreshTokenLogin', { token, refreshToken, tokenExpiry });

          return Promise.resolve();
        } else {
          const platformUrl =
            process.env.NODE_ENV === 'development'
              ? '/platformapi/'
              : config.PLATFORM_API_URL;
          const res = await axios.post(`${platformUrl}login/refresh`, {
            refreshToken: platformRefreshToken,
          });

          const { token, refreshToken } = res.data;

          context.commit('refreshTokenLogin', { token, refreshToken });

          return Promise.resolve();
        }
      } catch (error) {
        // Refresh failed, logout user
        context.commit('logout');
        context.commit('clearAuth');
        return Promise.reject(error);
      }
    }

    return Promise.reject(new Error('No refresh token available'));
  },
};

const mutations = {
  setTokenLogin(state, { token, tokenExpiry, refreshToken, email, companyId }) {
    state.token = token;
    state.refreshToken = refreshToken;
    state.userEmail = email;
    state.tokenExpiry = tokenExpiry;
    state.authChallenge = null;
    state.tokenCompanies = extractCompaniesFromToken(token);
    LocalStorageService.setAccessToken(token);
    LocalStorageService.setTokenExpiry(tokenExpiry);
    LocalStorageService.setRefreshToken(refreshToken);
    LocalStorageService.setUserEmail(email);
    LocalStorageService.setDchCompanyId(companyId);
  },
  refreshTokenLogin(state, { token, refreshToken, tokenExpiry }) {
    state.token = token;
    state.refreshToken = refreshToken;
    if (tokenExpiry != null) {
      state.tokenExpiry = tokenExpiry;
      LocalStorageService.setTokenExpiry(tokenExpiry);
    }
    state.tokenCompanies = extractCompaniesFromToken(token);
    LocalStorageService.setAccessToken(token);
    LocalStorageService.setRefreshToken(refreshToken);
  },
  logout(state) {
    state.token = null;
    state.userEmail = null;
    state.refreshToken = null;
    state.tokenExpiry = null;
    state.tokenCompanies = null;
    LocalStorageService.deleteAccessToken();
    LocalStorageService.deleteRefreshToken();
    LocalStorageService.deleteTokenExpiry();
    LocalStorageService.deleteUserEmail();
  },
  setAdminTokens(state, tokens) {
    state.authIdToken = tokens.IdToken;
    state.authAccessToken = tokens.AccessToken;
    state.authRefreshToken = tokens.RefreshToken;
    state.authResetResponse = null;
    localStorage.setItem('authIdToken', tokens.IdToken);
    localStorage.setItem('authAccessToken', tokens.AccessToken);
    LocalStorageService.setAuthRefreshToken(tokens.RefreshToken);
  },
  setAdminChallenge(state, challenge) {
    state.authChallenge = challenge;
  },
  setAdminLogin(state, { token, tokenExpiry, refreshToken, email, companyId }) {
    state.token = token;
    state.authRefreshToken = refreshToken;
    state.userEmail = email;
    state.tokenExpiry = tokenExpiry;
    state.authChallenge = null;
    state.tokenCompanies = extractCompaniesFromToken(token);
    LocalStorageService.setAccessToken(token);
    LocalStorageService.setUserEmail(email);
    LocalStorageService.setTokenExpiry(tokenExpiry);
    // The refresh token is from Cognito, so we store it as authRefreshToken
    LocalStorageService.setAuthRefreshToken(refreshToken);
    LocalStorageService.setDchCompanyId(companyId);
  },
  setAdminTokenRefresh(state, { token, tokenExpiry }) {
    state.token = token;
    state.tokenExpiry = tokenExpiry;
    state.tokenCompanies = extractCompaniesFromToken(token);
    LocalStorageService.setAccessToken(token);
    LocalStorageService.setTokenExpiry(tokenExpiry);
  },
  clearAuth(state) {
    state.authIdToken = null;
    state.authAccessToken = null;
    state.authRefreshToken = null;
    state.authChallenge = null;
    state.authResetResponse = null;
    state.tokenCompanies = null;
    localStorage.removeItem('authIdToken');
    localStorage.removeItem('authAccessToken');
    LocalStorageService.deleteAuthRefreshToken();
    LocalStorageService.deleteDchCompanyId();
  },
  resetResponse(state, response) {
    state.authResetResponse = response;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
